import { httpAuth } from 'config/apiClient'
import endpoints from 'constants/endpoints'
import { IComment, RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { getChat, getPost, setNotificationChatModal, setNotificationPostModal } from 'redux/actions/feed'
import { setComments, setCommentsCounter, setViewedCommentsIds, updateViews } from 'redux/actions/comments'
import { getTask, patchTaskOverviewModal, setTaskOverviewModal } from 'redux/actions/tasks_v2'
import {
  createServiceNotificationResolver,
  INotificationEventHandler,
} from 'services/notifications/createServiceNotificationResolver'
import { INotification, IResolvedDestination } from 'services/notifications/types'
import { getCurrentEpoch, getUrlParamsString } from 'utils'

const COMMENTS_POST_DESTINATION: IResolvedDestination = { path: keys.ROUTE_NAMES.NEWS_FEED, replace: true }
const COMMENTS_TASK_DESTINATION: IResolvedDestination = { path: keys.ROUTE_NAMES.NEW_TASK_DASHBOARD, replace: true }
const CONTENT_UNAVAILABLE_MESSAGE = 'Content is no longer available'
const CHAT_COMMENT_CLIENT_CODES = new Set([18, 29])
const ANNOUNCEMENT_COMMENT_CLIENT_CODES = new Set([19, 20, 30])
const CHAT_COMMENT_SOURCE_CONTEXT_TYPES = new Set(['group', 'user', 'tag'])

const getString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed || null
}

const getStringOrNumberString = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return getString(value)
}

const getNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'string' ? Number(value) : value
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null
}

const getLowerString = (value: unknown): string | null => getString(value)?.toLowerCase() ?? null

const getObjectRef = (notification: INotification): Record<string, any> => {
  return notification.object_ref ?? notification.data?.object_ref ?? notification.data?.objectRef ?? {}
}

const getSubTaskIdFromOid = (oid?: string | null): string | null => {
  if (!oid || !oid.includes('_')) return null
  return oid.split('_')[0] || null
}

const getResultContextIdFromOid = (oid?: string | null): string | null => {
  if (!oid || !oid.includes('_')) return null
  return oid.split('_').slice(1).join('_') || null
}

const getCommentTargetType = (notification: INotification, objectRef: Record<string, any>, data: Record<string, any>) =>
  getLowerString(
    objectRef.object_type ??
      objectRef.objectType ??
      data.object_type ??
      data.objectType ??
      notification.source_type ??
      notification.sourceType ??
      notification.post_type ??
      objectRef.source_type ??
      objectRef.post_type ??
      objectRef.chat_type ??
      objectRef.target_type ??
      data.source_type ??
      data.sourceType ??
      data.post_type ??
      data.chat_type ??
      data.target_type
  )

const getChatCommentSourceContextId = (objectRef: Record<string, any>, data: Record<string, any>) => {
  const source = data.source ?? objectRef.source
  if (!source || typeof source !== 'object') return null

  const sourceType = getLowerString(source.type)
  return sourceType && CHAT_COMMENT_SOURCE_CONTEXT_TYPES.has(sourceType) ? getString(source.id) : null
}

const isChatCommentTarget = ({
  objectRef,
  data,
  targetType,
  clientCode,
}: {
  objectRef: Record<string, any>
  data: Record<string, any>
  targetType?: string | null
  clientCode?: number | null
}) =>
  Boolean(objectRef.chat_id ?? data.chat_id) ||
  Boolean(targetType?.includes('chat')) ||
  Boolean(clientCode != null && CHAT_COMMENT_CLIENT_CODES.has(clientCode))

const isAnnouncementCommentTarget = ({
  targetType,
  clientCode,
}: {
  targetType?: string | null
  clientCode?: number | null
}) =>
  Boolean(
    targetType?.includes('announcement') || (clientCode != null && ANNOUNCEMENT_COMMENT_CLIENT_CODES.has(clientCode))
  )

const getExistingChatFromStore = (dispatch: (action: any) => any, postId: string) =>
  dispatch((_dispatch: unknown, getState: () => RootState) =>
    getState().feed.chats.find((chat) => chat.post_id === postId)
  )

const getCommentRef = (notification: INotification) => {
  const objectRef = getObjectRef(notification)
  const data = notification.data ?? {}
  const followUpArgs = objectRef.follow_up_logic_args ?? data.follow_up_logic_args ?? {}
  const sourceContextId = getChatCommentSourceContextId(objectRef, data)

  const objectOid = getString(objectRef.oid)
  const dataOid = getString(data.oid)
  const oid =
    getString(dataOid?.includes('_') ? dataOid : null) ??
    getString(objectOid?.includes('_') ? objectOid : null) ??
    objectOid ??
    dataOid
  const postId = getString(objectRef.post_id ?? objectRef.chat_id ?? data.post_id ?? data.chat_id ?? oid)
  const groupId = getString(objectRef.group_id ?? data.group_id)
  const contextId = getString(
    objectRef.context_id ??
      objectRef.result_context_id ??
      data.context_id ??
      data.result_context_id ??
      getResultContextIdFromOid(oid)
  )
  const taskContextId = getString(
    objectRef.task_context_id ?? data.task_context_id ?? followUpArgs.task_context_id ?? groupId ?? contextId
  )
  const tid = getString(
    objectRef.tid ?? objectRef.task_id ?? data.tid ?? data.task_id ?? (dataOid?.includes('_') ? objectOid : null)
  )
  const stId =
    getString(objectRef.st_id ?? objectRef.subtask_id ?? data.st_id ?? data.subtask_id) ?? getSubTaskIdFromOid(oid)
  const cid = getString(objectRef.cid ?? objectRef.comment_id ?? data.cid ?? data.comment_id)
  const parentCid = getString(
    objectRef.parent_cid ?? objectRef.parent_comment_id ?? data.parent_cid ?? data.parent_comment_id
  )
  const replyCid = getString(objectRef.reply_cid ?? objectRef.reply_id ?? data.reply_cid ?? data.reply_id)
  const recurrenceTs =
    getStringOrNumberString(objectRef.recurrence_ts ?? data.recurrence_ts ?? followUpArgs.recurrence_ts) ?? null
  const mediaIndex = getNumber(objectRef.media_index ?? data.media_index ?? objectRef.index ?? data.index)
  const createdAtTs = getNumber(data.created_at_ts ?? objectRef.created_at_ts ?? notification.created_at_ts)
  const clientCode = getNumber(notification.client_code ?? data.client_code ?? objectRef.client_code)
  const targetType = getCommentTargetType(notification, objectRef, data)
  const isChatPost = isChatCommentTarget({ objectRef, data, targetType, clientCode })
  const isAnnouncementPost = isAnnouncementCommentTarget({ targetType, clientCode })

  return {
    oid,
    postId,
    groupId,
    contextId,
    taskContextId,
    tid,
    stId,
    cid,
    parentCid,
    replyCid,
    recurrenceTs,
    mediaIndex,
    createdAtTs,
    clientCode,
    isChatPost,
    isAnnouncementPost,
    sourceContextId,
  }
}

type CommentRef = ReturnType<typeof getCommentRef>

const getPostLikeCommentContextId = (ref: CommentRef) =>
  ref.groupId ?? ref.contextId ?? (ref.isChatPost ? ref.sourceContextId : null)
const getTaskId = (ref: CommentRef) => ref.tid ?? (ref.oid && !ref.oid.includes('_') ? ref.oid : null)
const getTargetCommentId = (ref: CommentRef) => ref.cid ?? ref.replyCid

const hasPostContextOrKnownChatType = (ref: CommentRef) =>
  Boolean(getPostLikeCommentContextId(ref) || ref.isChatPost || ref.isAnnouncementPost)

const hasPostCommentContract = (ref: CommentRef) => Boolean(ref.postId && hasPostContextOrKnownChatType(ref) && ref.cid)
const hasPostReplyContract = (ref: CommentRef) =>
  Boolean(ref.postId && hasPostContextOrKnownChatType(ref) && ref.parentCid && getTargetCommentId(ref))
const hasPostTargetCommentContract = (ref: CommentRef) =>
  Boolean(ref.postId && hasPostContextOrKnownChatType(ref) && getTargetCommentId(ref))

const hasTaskV2Contract = (ref: CommentRef) => {
  const taskId = getTaskId(ref)
  const contextId = ref.contextId ?? ref.groupId ?? ref.taskContextId
  const taskContextId = ref.taskContextId ?? ref.groupId ?? contextId

  return Boolean(taskId && contextId && taskContextId)
}

const hasTaskV2CommentContract = (ref: CommentRef) => Boolean(hasTaskV2Contract(ref) && getTargetCommentId(ref))
const hasTaskV2ReplyContract = (ref: CommentRef) =>
  Boolean(hasTaskV2Contract(ref) && ref.parentCid && getTargetCommentId(ref))
const hasTaskV2SubTaskContract = (ref: CommentRef) => Boolean(hasTaskV2CommentContract(ref) && (ref.stId || ref.oid))
const hasTaskV2MediaContract = (ref: CommentRef) => Boolean(hasTaskV2SubTaskContract(ref) && ref.parentCid)

const fetchCommentsIntoStore = async ({
  dispatch,
  oid,
  cid,
  parentCid,
  keyForSave,
  isReply = false,
  targetCreatedAtTs,
  viewedObjectId,
}: {
  dispatch: (action: any) => any
  oid: string
  cid?: string | null
  parentCid?: string | null
  keyForSave?: string
  isReply?: boolean
  targetCreatedAtTs?: number | null
  viewedObjectId?: string
}) => {
  const params = getUrlParamsString({
    oid,
    cid,
    created_at_ts: targetCreatedAtTs ?? getCurrentEpoch(),
    include_created_at_timestamp: Boolean(targetCreatedAtTs),
    query_older_comments: Boolean(cid),
    offset: null,
    limit: 12,
    parent_cid: parentCid,
  })

  try {
    const { data } = await httpAuth.get(endpoints.comments + params)
    const comments: IComment[] = Array.isArray(data?.comments) ? data.comments.reverse() : []
    if (!comments.length) return

    const key = keyForSave ?? (isReply && parentCid ? parentCid : oid)
    const counter =
      (typeof data?.counter === 'number' && data.counter) ||
      (typeof data?.comments_count === 'number' && data.comments_count) ||
      (typeof data?.count === 'number' && data.count) ||
      comments.length
    dispatch(setComments({ items: comments, hasMore: data.more, key, isReply }))
    dispatch(setCommentsCounter({ count: counter, key, isReply }))
    dispatch(setViewedCommentsIds({ key, ids: comments.map((comment) => comment.cid) }))
    dispatch(updateViews({ postID: viewedObjectId ?? oid, viewedIds: comments.map((comment) => comment.cid) }))
  } catch (error) {
    console.warn('[Notifications] failed to prefetch comments', error)
  }
}

// onBeforeNavigate already opens the post/chat/task modal directly whenever the notification's own
// fields satisfy the same "hasXContract" the caller passes here (even a failed fetch still surfaces
// as an in-modal "unavailable" state) — don't also navigate the whole page to the News Feed/
// Dashboard in that case, that's a redundant page load on top of the fetch we just made. Only fall
// back to `destination` when the contract genuinely doesn't hold, i.e. we couldn't attempt to open
// anything from this payload.
const resolveOrHandled = (hasContract: boolean, destination: IResolvedDestination): IResolvedDestination =>
  hasContract ? { handled: true } : destination

const getTargetPayload = (ref: CommentRef) => ({
  targetParentCommentId: ref.parentCid,
  targetCommentId: getTargetCommentId(ref),
})

const openPostComment = async (dispatch: (action: any) => any, ref: CommentRef, isChat = false) => {
  const postContextId = getPostLikeCommentContextId(ref)
  if (!ref.postId) return false
  const targetPayload = getTargetPayload(ref)

  if (isChat) {
    dispatch(setNotificationPostModal(null))
    dispatch(setTaskOverviewModal(null))
    dispatch(setNotificationChatModal({ id: ref.postId, ...targetPayload }))
    if (!postContextId) {
      dispatch(setNotificationChatModal({ id: ref.postId, ...targetPayload, error: CONTENT_UNAVAILABLE_MESSAGE }))
      return false
    }
    const chat =
      getExistingChatFromStore(dispatch, ref.postId) ??
      (await dispatch(getChat({ chat_id: ref.postId, contextId: postContextId })))
    if (!chat) {
      dispatch(setNotificationChatModal({ id: ref.postId, ...targetPayload, error: CONTENT_UNAVAILABLE_MESSAGE }))
      return false
    }
  } else {
    if (!postContextId) return false
    dispatch(setNotificationChatModal(null))
    dispatch(setTaskOverviewModal(null))
    dispatch(setNotificationPostModal({ id: ref.postId, ...targetPayload }))
    const post = await dispatch(getPost({ postId: ref.postId, contextId: postContextId }))
    if (!post) {
      dispatch(setNotificationPostModal({ id: ref.postId, ...targetPayload, error: CONTENT_UNAVAILABLE_MESSAGE }))
      return false
    }
  }

  if (ref.cid || ref.parentCid) {
    await fetchCommentsIntoStore({
      dispatch,
      oid: ref.postId,
      cid: ref.parentCid ?? getTargetCommentId(ref),
      targetCreatedAtTs: ref.createdAtTs,
      viewedObjectId: ref.postId,
    })
  }

  if (ref.parentCid && getTargetCommentId(ref)) {
    await fetchCommentsIntoStore({
      dispatch,
      oid: ref.parentCid,
      cid: getTargetCommentId(ref),
      parentCid: ref.parentCid,
      keyForSave: ref.parentCid,
      isReply: true,
      targetCreatedAtTs: ref.createdAtTs,
      viewedObjectId: ref.postId,
    })
  }

  return true
}

const openTaskComment = async (dispatch: (action: any) => any, ref: CommentRef, isReply = false) => {
  const taskId = getTaskId(ref)
  const contextId = ref.contextId ?? ref.groupId ?? ref.taskContextId
  const taskContextId = ref.taskContextId ?? ref.groupId ?? contextId
  if (!taskId || !contextId || !taskContextId) return false

  dispatch(setNotificationPostModal(null))
  dispatch(setNotificationChatModal(null))
  dispatch(
    setTaskOverviewModal({
      tid: taskId,
      task_context_id: taskContextId,
      result_context_id: contextId,
      recurrence_ts: ref.recurrenceTs,
      targetSubtaskId: ref.stId,
      targetParentCommentId: ref.parentCid,
      targetCommentId: getTargetCommentId(ref),
      targetMediaIndex: ref.mediaIndex,
      isLoading: true,
      error: null,
    })
  )

  const response = await dispatch(
    getTask({
      tid: taskId,
      task_context_id: taskContextId,
      result_context_id: contextId,
      recurrence_ts: ref.recurrenceTs,
    })
  )

  if (response.task && ref.oid) {
    if (isReply && ref.parentCid) {
      await fetchCommentsIntoStore({
        dispatch,
        oid: ref.oid,
        cid: ref.parentCid,
        keyForSave: ref.oid,
        targetCreatedAtTs: ref.createdAtTs,
        viewedObjectId: ref.oid,
      })
      await fetchCommentsIntoStore({
        dispatch,
        oid: ref.parentCid,
        cid: getTargetCommentId(ref),
        parentCid: ref.parentCid,
        keyForSave: ref.parentCid,
        isReply: true,
        targetCreatedAtTs: ref.createdAtTs,
        viewedObjectId: ref.oid,
      })
    } else {
      await fetchCommentsIntoStore({
        dispatch,
        oid: ref.oid,
        cid: getTargetCommentId(ref),
        parentCid: ref.parentCid,
        keyForSave: ref.parentCid ? `${ref.oid}-${ref.parentCid}` : undefined,
        targetCreatedAtTs: ref.createdAtTs,
        viewedObjectId: ref.oid,
      })
    }
  }

  dispatch(patchTaskOverviewModal({ isLoading: false, error: response.task ? null : 'Unable to load task' }))

  return Boolean(response.task)
}

const postCommentEvent: INotificationEventHandler = {
  eventTypes: ['comment_on_post'],
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return

    const ref = getCommentRef(notification)
    if (!hasPostCommentContract(ref)) return

    await openPostComment(dispatch, ref, ref.isChatPost || ref.isAnnouncementPost)
  },
  async resolve(notification): Promise<IResolvedDestination> {
    if (notification.target?.route) return { path: notification.target.route, replace: true }
    return resolveOrHandled(hasPostCommentContract(getCommentRef(notification)), COMMENTS_POST_DESTINATION)
  },
}

const commentReactionEvent: INotificationEventHandler = {
  eventTypes: ['comment_reaction'],
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return

    const ref = getCommentRef(notification)
    if (!hasPostCommentContract(ref)) return
    await openPostComment(dispatch, ref, true)
  },
  async resolve(notification): Promise<IResolvedDestination> {
    return resolveOrHandled(hasPostCommentContract(getCommentRef(notification)), COMMENTS_POST_DESTINATION)
  },
}

const postReplyEvent: INotificationEventHandler = {
  eventTypes: ['post_comment_reply'],
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return

    const ref = getCommentRef(notification)

    if (ref.parentCid) {
      if (!hasPostReplyContract(ref)) return
    } else if (!hasPostTargetCommentContract(ref)) {
      return
    }

    await openPostComment(dispatch, ref, ref.isChatPost || ref.isAnnouncementPost)
  },
  async resolve(notification): Promise<IResolvedDestination> {
    if (notification.target?.route) return { path: notification.target.route, replace: true }
    const ref = getCommentRef(notification)
    const hasContract = ref.parentCid ? hasPostReplyContract(ref) : hasPostTargetCommentContract(ref)
    return resolveOrHandled(hasContract, COMMENTS_POST_DESTINATION)
  },
}

const subTaskCommentEvent: INotificationEventHandler = {
  eventTypes: ['comment_on_subtask_creator', 'comment_on_subtask_team_members', 'comment_on_subtask_group_members'],
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return

    const ref = getCommentRef(notification)
    if (!hasTaskV2SubTaskContract(ref)) return
    await openTaskComment(dispatch, ref)
  },
  async resolve(notification): Promise<IResolvedDestination> {
    if (notification.target?.route) return { path: notification.target.route, replace: true }
    return resolveOrHandled(hasTaskV2SubTaskContract(getCommentRef(notification)), COMMENTS_TASK_DESTINATION)
  },
}

const subTaskReplyEvent: INotificationEventHandler = {
  eventTypes: ['subtask_comment_reply'],
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return

    const ref = getCommentRef(notification)
    if (!hasTaskV2ReplyContract(ref)) return
    await openTaskComment(dispatch, ref, true)
  },
  async resolve(notification): Promise<IResolvedDestination> {
    if (notification.target?.route) return { path: notification.target.route, replace: true }
    return resolveOrHandled(hasTaskV2ReplyContract(getCommentRef(notification)), COMMENTS_TASK_DESTINATION)
  },
}

const TASK_V2_COMMENT_REPLY_EVENTS = [
  'media_comment_reply',
  'result_comment_reply',
  'task_result_context_comment_reply',
]
const TASK_V2_COMMENT_MEDIA_EVENTS = ['comment_on_media', 'media_comment_reply']

const getTaskV2CommentContract = (notification: INotification, ref: CommentRef) => {
  const eventType = notification.event_type.trim().toLowerCase()
  const needsReplyContract = TASK_V2_COMMENT_REPLY_EVENTS.includes(eventType)
  const needsMediaContract = TASK_V2_COMMENT_MEDIA_EVENTS.includes(eventType)

  const hasExpectedContract = needsMediaContract
    ? hasTaskV2MediaContract(ref)
    : needsReplyContract
    ? hasTaskV2ReplyContract(ref)
    : hasTaskV2SubTaskContract(ref)

  return { hasExpectedContract, needsReplyContract }
}

const taskV2CommentEvent: INotificationEventHandler = {
  eventTypes: [
    'comment_on_media',
    'media_comment_reply',
    'comment_on_result',
    'result_comment_reply',
    'comment_on_task_result_context',
    'task_result_context_comment_reply',
  ],
  async onBeforeNavigate(notification, { dispatch }) {
    if (!dispatch) return

    const ref = getCommentRef(notification)
    const { hasExpectedContract, needsReplyContract } = getTaskV2CommentContract(notification, ref)
    if (!hasExpectedContract) return

    await openTaskComment(dispatch, ref, needsReplyContract)
  },
  async resolve(notification): Promise<IResolvedDestination> {
    if (notification.target?.route) return { path: notification.target.route, replace: true }
    const { hasExpectedContract } = getTaskV2CommentContract(notification, getCommentRef(notification))
    return resolveOrHandled(hasExpectedContract, COMMENTS_TASK_DESTINATION)
  },
}

const fallbackEvent: INotificationEventHandler = {
  eventTypes: [],
  async resolve(): Promise<IResolvedDestination> {
    return COMMENTS_POST_DESTINATION
  },
}

export const commentsResolver = createServiceNotificationResolver({
  id: 'comments',
  service: 'comments',
  events: [
    postCommentEvent,
    commentReactionEvent,
    postReplyEvent,
    subTaskCommentEvent,
    subTaskReplyEvent,
    taskV2CommentEvent,
  ],
  fallback: fallbackEvent,
})
